import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { History, Camera, RotateCcw, Trash2, Eye, Clock, FileText } from 'lucide-react';

// Storage key for snapshots
const getSnapshotsKey = (bookId) => `eskrybe-snapshots-${bookId}`;

// Get all snapshots for a book
export const getSnapshots = (bookId) => {
    const key = getSnapshotsKey(bookId);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : {};
};

// Get snapshots for a specific chapter
export const getChapterSnapshots = (bookId, chapterId) => {
    const allSnapshots = getSnapshots(bookId);
    return allSnapshots[chapterId] || [];
};

// Create a new snapshot
export const createSnapshot = (bookId, chapterId, chapterTitle, content, wordCount, note = '') => {
    const allSnapshots = getSnapshots(bookId);
    const chapterSnapshots = allSnapshots[chapterId] || [];
    
    const snapshot = {
        id: Date.now().toString(),
        chapterId,
        chapterTitle,
        content,
        wordCount,
        note,
        createdAt: new Date().toISOString()
    };
    
    // Keep last 20 snapshots per chapter
    chapterSnapshots.unshift(snapshot);
    if (chapterSnapshots.length > 20) {
        chapterSnapshots.pop();
    }
    
    allSnapshots[chapterId] = chapterSnapshots;
    localStorage.setItem(getSnapshotsKey(bookId), JSON.stringify(allSnapshots));
    
    return snapshot;
};

// Delete a snapshot
export const deleteSnapshot = (bookId, chapterId, snapshotId) => {
    const allSnapshots = getSnapshots(bookId);
    if (allSnapshots[chapterId]) {
        allSnapshots[chapterId] = allSnapshots[chapterId].filter(s => s.id !== snapshotId);
        localStorage.setItem(getSnapshotsKey(bookId), JSON.stringify(allSnapshots));
    }
};

// Snapshot Button Component (for chapter cards/sidebar)
export const SnapshotButton = ({ bookId, chapter, currentContent, onRestore }) => {
    const [open, setOpen] = useState(false);
    const [snapshots, setSnapshots] = useState([]);
    const [selectedSnapshot, setSelectedSnapshot] = useState(null);
    const [note, setNote] = useState('');

    useEffect(() => {
        if (open) {
            setSnapshots(getChapterSnapshots(bookId, chapter.id));
        }
    }, [open, bookId, chapter.id]);

    const handleCreate = () => {
        const wordCount = currentContent ? currentContent.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length : 0;
        createSnapshot(bookId, chapter.id, chapter.title, currentContent, wordCount, note);
        setSnapshots(getChapterSnapshots(bookId, chapter.id));
        setNote('');
        toast.success('Snapshot created');
    };

    const handleRestore = (snapshot) => {
        if (confirm('Restore this snapshot? Your current content will be replaced.')) {
            onRestore(snapshot.content);
            toast.success('Snapshot restored');
            setOpen(false);
        }
    };

    const handleDelete = (snapshotId) => {
        if (confirm('Delete this snapshot?')) {
            deleteSnapshot(bookId, chapter.id, snapshotId);
            setSnapshots(getChapterSnapshots(bookId, chapter.id));
            toast.success('Snapshot deleted');
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1" data-testid="snapshot-btn">
                    <History className="h-4 w-4" />
                    <span className="hidden sm:inline">Snapshots</span>
                    {snapshots.length > 0 && (
                        <Badge variant="secondary" className="ml-1 text-xs">
                            {snapshots.length}
                        </Badge>
                    )}
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <History className="h-5 w-5 text-accent" />
                        Version History - {chapter.title}
                    </DialogTitle>
                </DialogHeader>

                <div className="mt-4">
                    {/* Create New Snapshot */}
                    <div className="p-4 bg-accent/5 rounded-lg mb-4">
                        <div className="flex items-center gap-3">
                            <input
                                type="text"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Optional note (e.g., 'Before major revision')"
                                className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
                            />
                            <Button onClick={handleCreate} size="sm">
                                <Camera className="h-4 w-4 mr-2" />
                                Take Snapshot
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            Snapshots save the current state of this chapter. You can restore any previous version.
                        </p>
                    </div>

                    {/* Snapshots List */}
                    {snapshots.length === 0 ? (
                        <div className="text-center py-8">
                            <History className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                            <p className="text-sm text-muted-foreground">No snapshots yet</p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Create a snapshot to save a version of your work
                            </p>
                        </div>
                    ) : (
                        <ScrollArea className="h-96">
                            <div className="space-y-2">
                                {snapshots.map((snapshot, index) => (
                                    <div 
                                        key={snapshot.id}
                                        className={`
                                            p-3 rounded-lg border transition-colors cursor-pointer
                                            ${selectedSnapshot?.id === snapshot.id 
                                                ? 'border-accent bg-accent/5' 
                                                : 'border-border hover:border-accent/50'}
                                        `}
                                        onClick={() => setSelectedSnapshot(
                                            selectedSnapshot?.id === snapshot.id ? null : snapshot
                                        )}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium text-sm">
                                                        {formatDate(snapshot.createdAt)}
                                                    </span>
                                                    {index === 0 && (
                                                        <Badge variant="outline" className="text-xs">Latest</Badge>
                                                    )}
                                                </div>
                                                {snapshot.note && (
                                                    <p className="text-sm text-muted-foreground mt-1 ml-6">
                                                        {snapshot.note}
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground mt-1 ml-6">
                                                    {snapshot.wordCount?.toLocaleString() || 0} words
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={(e) => { e.stopPropagation(); handleRestore(snapshot); }}
                                                    title="Restore"
                                                >
                                                    <RotateCcw className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive"
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(snapshot.id); }}
                                                    title="Delete"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Preview when selected */}
                                        {selectedSnapshot?.id === snapshot.id && (
                                            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                                    <span className="text-xs font-medium">Preview</span>
                                                </div>
                                                <div 
                                                    className="text-xs text-muted-foreground line-clamp-6 prose prose-sm max-w-none"
                                                    dangerouslySetInnerHTML={{ 
                                                        __html: snapshot.content?.substring(0, 500) || '<em>Empty</em>' 
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Dashboard Snapshot Dropdown (for chapter list)
export const ChapterSnapshotDropdown = ({ bookId, chapter, currentContent, onRestore }) => {
    return (
        <SnapshotButton 
            bookId={bookId} 
            chapter={chapter} 
            currentContent={currentContent}
            onRestore={onRestore}
        />
    );
};

export default SnapshotButton;
